import { type FC } from 'react'
import { observer } from 'mobx-react-lite'
import classnames from 'classnames'
import { TextField, makeTextFieldInteractions, makeTextFieldTooltip } from 'shared/ui/TextField'
import { Typography } from 'shared/ui'
import type { ZIndex } from 'shared/constants/zIndex'
import { TooltipEmailDomain } from 'shared/ui/Tooltip/ui/TooltipEmailDomain/TooltipEmailDomain'
import type { IntegrationProperty } from 'entities/Integrations/model'
import { useEntitiesContext } from 'entities/EntitiesExport'
import styles from './styles.module.scss'

export type IItemRowProps = {
  item: IntegrationProperty
  clearAction?: (key: string) => void
  labelType?: 'simple' | 'field'
  zIndex?: ZIndex
}

export const ItemRow: FC<IItemRowProps> = observer(
  ({ item, clearAction, labelType = 'field', zIndex }) => {
    const { usersStore } = useEntitiesContext()
    const user = usersStore.user
    const isUserViewOnly = !!user?.isViewOnlyRole

    return (
      <div
        key={item.key}
        className={classnames(styles.itemRow, {
          [styles.emptyPadding]: labelType === 'field',
        })}
      >
        {labelType === 'simple' && (
          <Typography
            width={110}
            variant={'body-md-regular'}
            color={'var(--content-primary-tertiary)'}
            tag={'div'}
            tooltipProps={{
              label: item.label,
              zIndex,
            }}
            ellipsis
            ariaLabel={'label'}
          >
            {item.label || '-'}
          </Typography>
        )}
        {labelType === 'field' && (
          <TextField
            className={styles.label}
            size='small'
            variant='outline'
            readOnly
            value={item.label || '-'}
            rightActions={[
              makeTextFieldInteractions({
                type: 'clear',
                active: !isUserViewOnly,
                action: () => clearAction?.(item.key),
                zIndex,
              }),
            ]}
          />
        )}

        <TextField
          className={styles.value}
          size='small'
          variant='outline'
          readOnly
          value={item.value || '-'}
          rightActions={[
            makeTextFieldInteractions(
              { type: 'copy', active: !!item.value, zIndex },
              { type: 'email', active: item.key === 'email', zIndex }
            ),
          ]}
          mainActions={[
            makeTextFieldTooltip(
              item.key === 'email' && item.value
                ? {
                    label: <TooltipEmailDomain email={item.value} />,
                    interactive: true,
                    zIndex: zIndex,
                  }
                : { zIndex: zIndex }
            ),
          ]}
        />
      </div>
    )
  }
)
